import React, { useContext, useState } from 'react';
import {
  Button,
  Checkbox,
  Popconfirm,
  Progress,
  Segmented,
  Space,
  Tag,
  Typography,
} from 'antd';

import AccountPlanContext from '@totem/components/accountPlan/AccountPlanContext';
import PlanCard from '@totem/components/accountPlan/PlanCard';
import { ActionItem } from '@totem/components/accountPlan/types';
import {
  PRIORITY_LABELS,
  PriorityBadge,
  shortDate,
  STATUS_COLORS,
  STATUS_LABELS,
} from '@totem/components/accountPlan/utilities';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';

type Filter = 'all' | 'open' | 'done';

type ActionRow = ActionItem & { recommended: boolean };

const ActionPlanCard = () => {
  const { data, openEditor, savePlan, saving } = useContext(AccountPlanContext);
  const [filter, setFilter] = useState<Filter>('open');
  if (!data) {
    return null;
  }
  const { plan, summary } = data;

  const rows: ActionRow[] = [
    ...plan.actions.map((action) => ({ ...action, recommended: false })),
    ...summary.recommendedActions.map((action) => ({
      ...action,
      recommended: true,
    })),
  ].filter((action) => {
    if (filter === 'open') {
      return action.status !== 'complete';
    }
    if (filter === 'done') {
      return action.status === 'complete';
    }
    return true;
  });

  const toggleDone = (action: ActionItem, done: boolean) =>
    savePlan({
      ...plan,
      actions: plan.actions.map((item) =>
        item.id === action.id
          ? {
              ...item,
              status: done ? 'complete' : 'inProgress',
              percentComplete: done ? 100 : item.percentComplete,
            }
          : item,
      ),
    });

  const adopt = (action: ActionItem) =>
    savePlan({
      ...plan,
      actions: [...plan.actions, { ...action, source: 'portal' }],
    });

  const remove = (id: string) =>
    savePlan({
      ...plan,
      actions: plan.actions.filter((action) => action.id !== id),
    });

  const columns = [
    {
      title: 'Done',
      dataIndex: 'status',
      width: 60,
      render: (status: string, action: ActionRow) =>
        action.recommended ? null : (
          <Checkbox
            checked={status === 'complete'}
            disabled={saving}
            onChange={(event) => toggleDone(action, event.target.checked)}
          />
        ),
    },
    {
      title: 'Action',
      dataIndex: 'action',
      render: (text: string, action: ActionRow) => (
        <>
          <Typography.Text delete={action.status === 'complete'}>
            {text}
          </Typography.Text>{' '}
          {action.source === 'portal' && <Tag color="blue">Portal</Tag>}
          {action.linked && (
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {action.linked}
              </Typography.Text>
            </div>
          )}
        </>
      ),
    },
    {
      title: 'Assigned To',
      dataIndex: 'assignedTo',
      render: (who: string) => who || '—',
    },
    {
      title: 'Assigned',
      dataIndex: 'dateAssigned',
      render: (date?: string) => shortDate(date),
    },
    {
      title: 'Due',
      dataIndex: 'dateDue',
      render: (date?: string) => shortDate(date),
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      render: (priority: string) => (
        <PriorityBadge
          priority={priority}
          label={PRIORITY_LABELS[priority] ?? priority}
        />
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'statusLabel',
      render: (status: string, action: ActionRow) =>
        action.recommended ? (
          <Tag>Recommended</Tag>
        ) : (
          <Tag color={STATUS_COLORS[status]}>
            {STATUS_LABELS[status] ?? status}
          </Tag>
        ),
    },
    {
      title: '% Complete',
      dataIndex: 'percentComplete',
      width: 120,
      render: (percent: number) => <Progress percent={percent} size="small" />,
    },
    { title: 'Notes', dataIndex: 'notes' },
    {
      title: '',
      dataIndex: 'id',
      render: (id: string, action: ActionRow) =>
        action.recommended ? (
          <Button
            type="link"
            size="small"
            disabled={saving}
            onClick={() => adopt(action)}
          >
            Add to plan
          </Button>
        ) : (
          <Space size={0}>
            <Button
              type="link"
              size="small"
              onClick={() => openEditor({ section: 'action', itemId: id })}
            >
              Edit
            </Button>
            <Popconfirm
              title="Remove this action?"
              onConfirm={() => remove(id)}
            >
              <Button type="link" size="small" danger>
                Remove
              </Button>
            </Popconfirm>
          </Space>
        ),
    },
  ];

  return (
    <PlanCard
      variant="manual"
      onEdit={() => openEditor({ section: 'action' })}
      title="Action Plan"
      extra={
        <Space>
          <Typography.Text type="secondary">
            {plan.actions.length} on plan · {summary.recommendedActions.length}{' '}
            recommended
          </Typography.Text>
        </Space>
      }
    >
      <Segmented<Filter>
        style={{ marginBottom: 8 }}
        value={filter}
        onChange={setFilter}
        options={[
          { label: 'Open', value: 'open' },
          { label: 'Done', value: 'done' },
          { label: 'All', value: 'all' },
        ]}
      />
      <IBTable
        size="small"
        rowKey="id"
        columns={columns}
        dataSource={rows}
        scroll={{ x: 1100 }}
        pagination={{
          defaultPageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: [10, 25, 50],
        }}
        locale={{ emptyText: 'No actions' }}
      />
    </PlanCard>
  );
};

export default ActionPlanCard;
